import {Employee, INeighbourhood} from "../../api/Model";
import {HierarchyEmployee, relativesSequence, SubtreeSize} from "../../util/EmployeeHierarchy";
import {getOrSet} from "../../util/MapUtil";
import {numberCmp} from "../../util/SortUtil";

export interface GuessTeam {
    commonManager: HierarchyEmployee;
    distance: number;
    withManager: boolean;
    teammates: Employee[];
    weight: number;
    localWeight: number;
}

export interface NeighbourhoodGuess {
    neighbourhood: INeighbourhood;
    teams: GuessTeam[];
    weight: number;
    localWeight: number;
}

function getOrCreate(
    map: Map<INeighbourhood['key'], { neighbourhood: INeighbourhood, teams: Map<Employee['id'], GuessTeam> }>,
    neighbourhood: INeighbourhood,
    manager: HierarchyEmployee & { distance: number }
) {
    const guess = getOrSet(map, neighbourhood.key,
        () => ({
            neighbourhood,
            teams: new Map(),
        })
    );

    const team = getOrSet(guess.teams, manager.id, () => ({
        commonManager: manager,
        distance: manager.distance,
        withManager: false,
        teammates: [],
        weight: 0,
        localWeight: 0,
    }));

    return team;
}

export function guessNeighbourhoods(
    employeeNode: HierarchyEmployee,
    globalTeamSize: SubtreeSize,
    localTeamSize: SubtreeSize,
): NeighbourhoodGuess[] {
    const guessedNeighbourhoods: Map<INeighbourhood['key'], {
        neighbourhood: INeighbourhood,
        teams: Map<Employee['id'], GuessTeam>
    }> = new Map();

    for (const relative of relativesSequence(employeeNode)) {
        if (relative.type === 'node' && relative.neighbourhoods.length > 0) {
            switch (relative.kind) {
                case "self":
                    // skip
                    break;
                case "ancestor":
                    relative.neighbourhoods.forEach(neighbourhood => {
                        const team = getOrCreate(guessedNeighbourhoods, neighbourhood, relative);
                        team.withManager = true;
                    })
                    break;
                case "descendant":
                case "sibling":
                    const commonAncestor = relative.commonAncestor;
                    if (commonAncestor) {
                        relative.neighbourhoods.forEach(neighbourhood => {
                            const team = getOrCreate(guessedNeighbourhoods, neighbourhood, commonAncestor);
                            team.teammates.push(relative);
                        })
                    }
                    break;
            }
        }
    }

    return [...guessedNeighbourhoods.values()]
        .map(it => {
            const teams = [...it.teams.values()]
                .map(team => {
                    const absoluteWeight = team.teammates.reduce<number>(
                        (acc, _, index) => acc + 1.0 / (index + 1),
                        team.withManager ? 2.0 : 0,
                    );
                    return {
                        ...team,
                        weight: Math.round(globalTeamSize.rootSize * absoluteWeight / globalTeamSize.getSize(team.commonManager)),
                        localWeight: Math.round(localTeamSize.rootSize * absoluteWeight / localTeamSize.getSize(team.commonManager)),
                    };
                })
                .sort(numberCmp('weight', true));

            return {
                neighbourhood: it.neighbourhood,
                teams: teams,
                weight: teams.reduce((acc, it) => acc + it.weight, 0),
                localWeight: teams.reduce((acc, it) => acc + it.localWeight, 0),
            };
        })
        .sort(numberCmp('weight', true))
        .slice(0, 5)
}